import { Injectable } from '@angular/core';

import type { Observable } from 'rxjs';
import { BehaviorSubject, Subject } from 'rxjs';

import { LocalStorageService } from '@recruitee/common';
import type { RequisitionColumnId } from '@recruitee/requisitions-types';
import type { SortDirection } from '@recruitee/search-types';
import type {
  DataTableColumn,
  DataTableColumnGeometryChange,
  DataTableColumnsCache,
} from '@recruitee/user-interface';

import type { RequisitionsTableColumn } from '../requisitions.types';
import { RequisitionsListColumnsModelService } from './requisitions-list-columns-model.service';

export type RequisitionsTableConfig = {
  allColumns: RequisitionsTableColumn[];
  visibleColumns: RequisitionsTableColumn[];
  autoFit: boolean;
};

@Injectable()
export class RequisitionsListTableService {
  public readonly requisitionsTableLocalStorageKey: string = 'REQUISITIONS_TABLE_CONFIG';

  public tableConfig$: Observable<RequisitionsTableConfig>;

  public get table(): RequisitionsTableConfig {
    return this._tableConfig$.getValue();
  }

  private _tableConfig$: BehaviorSubject<RequisitionsTableConfig>;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private tableColumnsService: RequisitionsListColumnsModelService,
    private localStorage: LocalStorageService,
  ) {
    this.calculateVisibleColumns();
    this.tableConfig$ = this._tableConfig$.asObservable();
  }

  public handleGeometryChange(event: DataTableColumnGeometryChange): void {
    const allColumns = this.table.allColumns.map(col => {
      const modified = event.columns.find(c => c.id === col.id);
      return {
        ...col,
        geometry: modified ? modified.geometry : col.geometry,
      };
    });
    this.changeTableConfig({ columns: allColumns, autoFit: event.autofit });
  }

  public toggleColumnSort(id: RequisitionColumnId, sortOrder: SortDirection): void {
    this.changeTableConfig({
      columns: this.table.allColumns.map(column =>
        column.id === id
          ? {
              ...column,
              sort: sortOrder,
            }
          : {
              ...column,
              sort: undefined,
            },
      ),
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public calculateVisibleColumns(): void {
    const cachedTableOptions: {
      autoFit: boolean;
      columns: DataTableColumnsCache;
    } = this.localStorage.get(this.requisitionsTableLocalStorageKey, {
      autoFit: true,
      columns: this.toCacheableMap(this.tableColumnsService.columns),
    });

    const allColumns = this.tableColumnsService.columns
      .map(column => {
        const cachedColumn = cachedTableOptions.columns[column.id];
        return cachedColumn
          ? {
              ...column,
              visible: cachedColumn.visible,
              index: cachedColumn.index,
            }
          : column;
      })
      .sort(
        (
          a: DataTableColumn<RequisitionColumnId> | any,
          b: DataTableColumn<RequisitionColumnId> | any,
        ) => a.index - b.index,
      );

    const visibleColumns = allColumns.filter(column => column.visible);

    this._tableConfig$ = new BehaviorSubject<RequisitionsTableConfig>({
      allColumns,
      visibleColumns,
      autoFit: cachedTableOptions.autoFit,
    });
  }

  public changeTableConfig(options: {
    autoFit?: boolean;
    columns?: RequisitionsTableColumn[];
  }): void {
    const allColumns = options.columns ? options.columns : this.table.allColumns;
    const visibleColumns = allColumns.filter(c => c.visible);

    const autoFit = options.autoFit !== undefined ? options.autoFit : this.table.autoFit;

    this.localStorage.set(this.requisitionsTableLocalStorageKey, {
      autoFit,
      columns: this.toCacheableMap(allColumns, visibleColumns),
    });

    this._tableConfig$.next({
      ...this.table,
      allColumns,
      visibleColumns,
      autoFit,
    });
  }

  private toCacheableMap(
    columns: RequisitionsTableColumn[],
    visibleColumns?: RequisitionsTableColumn[],
  ): DataTableColumnsCache {
    return columns.reduce(
      (agg, c, index) => ({
        [c.id]: {
          index,
          visible: visibleColumns ? visibleColumns.includes(c) : c.visible,
        },
        ...agg,
      }),
      {},
    );
  }
}
